import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AbstractWorkflowService, ConfigurationService, UUID, WebSocketService} from '@nh/angular-tses';
import {DepartureService} from 'app/core/services/departure.service';
import {SnapshotService} from 'app/core/services/snapshot.service';
import {DepartureProperties} from 'app/diary/properties/departure-properties';
import {DepartureFragment} from 'app/fragment/types/departure-fragment';
import {toArray} from 'app/utils/array-utils';
import {BehaviorSubject, Observable, Subject, Subscription} from 'rxjs';
import {DepartureComment} from '../comment/departure-comment';
import {DeparturePermissions} from '../permissions/departure-permissions';
import {DepartureWorkflowState} from '../workflow/departure-workflow-state';
import {DiaryEvent} from './diary-event';
import {DiaryEventType} from './diary-event-type';
import {departureIdFromSnapshot, DiarySnapshot} from './diary-snapshot';
import {DepartureReasonCodes} from './reason-codes/reason-codes.service';

@Injectable({
  providedIn: 'root'
})
export class DiaryService extends AbstractWorkflowService {
  private readonly latestDiarySnapshotSubject: BehaviorSubject<DiarySnapshot> = new BehaviorSubject(null);
  private readonly latestDiaryEventSubject: Subject<DiaryEvent> = new Subject();

  private departureId: UUID = null;

  private websocketSubscription: Subscription;

  constructor(
    snackbar: MatSnackBar,
    configurationService: ConfigurationService,
    http: HttpClient,
    private departureService: DepartureService,
    private websocketService: WebSocketService,
    private snapshotService: SnapshotService
  ) {
    super(snackbar, configurationService, http);

    this.departureService.onSelection((dep: DepartureFragment) => {
      if (dep) {
        const changedDeparture: boolean = !dep.id.equals(this.departureId);
        this.departureId = dep.id;
        this.getLatestDiarySnapshot(dep.id).then((snapshot: DiarySnapshot) =>
          this.broadcastSnapshot(snapshot, changedDeparture)
        );
      } else {
        this.departureId = null;
        this.latestDiarySnapshotSubject.next(null);
      }
    });
    this.websocketService.onConnection(this.onWebsocketConnect.bind(this));
  }

  private onWebsocketConnect(connected: boolean): void {
    if (this.websocketSubscription) {
      this.websocketSubscription.unsubscribe();
      this.websocketSubscription = null;
    }

    if (connected) {
      this.websocketSubscription = this.websocketService.subscribe('/topic/diary', (data: DiaryEvent) => {
        this.broadcastSnapshot(data);
        this.broadcastEvent(data);
      });
    }
  }

  public getLatestDiarySnapshot(departureId: UUID, ...types: DiaryEventType[]): Promise<DiarySnapshot> {
    return this.getDiarySnapshotAtTime(
      departureId,
      this.snapshotService.viewingSnapshot() ? this.snapshotService.getSnapshotTimestamp() : null,
      ...types
    );
  }

  public getDiarySnapshotAtTime(departureId: UUID, time: number, ...types: DiaryEventType[]): Promise<DiarySnapshot> {
    if (!departureId) {
      return Promise.reject('departureId was null');
    }

    const params: {[param: string]: string | string[]} = {};
    if (typeof time === 'number') {
      params.time = time.toString();
    }
    if (types.length) {
      params.types = types.join();
    }

    return this._http.get<DiarySnapshot>(`${this.config.apiHost}/diary/${departureId.value}`, {params}).toPromise();
  }

  public getDiaryEvents(departureId: UUID, ...types: DiaryEventType[]): Promise<DiaryEvent[]> {
    const params: {[param: string]: string | string[]} = {
      from: '0',
      to: String(
        this.snapshotService.viewingSnapshot() ? this.snapshotService.getSnapshotTimestamp() : Number.MAX_SAFE_INTEGER
      )
    };
    if (types.length) {
      params.types = types.join();
    }

    return this._http
      .get<DiaryEvent[]>(`${this.config.apiHost}/diary/${departureId.value}/events`, {params})
      .toPromise();
  }

  public createDiaryEvent(departureId: UUID, snapshot: DiarySnapshot): Promise<DiaryEvent[]> {
    return this._http
      .post<DiaryEvent[]>(`${this.config.apiHost}/diary/${departureId.value}`, snapshot)
      .toPromise()
      .then((events: DiaryEvent[]) => {
        toArray(events).forEach((event: DiaryEvent) => {
          this.broadcastSnapshot(event);
          this.broadcastEvent(event);
        });
        return events;
      });
  }

  public getLatestWorkflowState(departureId: UUID): Promise<DepartureWorkflowState> {
    return this._getState(departureId);
  }

  public getWorkflowStateAtTime(departureId: UUID, time: number): Promise<DepartureWorkflowState> {
    return this._getState(departureId, time);
  }

  public getWorkflowStateEvents(
    departureId: UUID,
    from: number = 0,
    to: number = Number.MAX_SAFE_INTEGER
  ): Promise<DepartureWorkflowState[]> {
    return this._getStateEvents(departureId, from, to);
  }

  public getLatestComment(departureId: UUID): Promise<DepartureComment> {
    return this.getCommentAtTime(departureId);
  }

  public getCommentAtTime(departureId: UUID, time?: number): Promise<DepartureComment> {
    return this._getEventOfTypeAtTime(departureId, `${this.config.apiHost}/comments/${departureId.value}`, time);
  }

  public getCommentEvents(
    departureId: UUID,
    from: number = 0,
    to: number = Number.MAX_SAFE_INTEGER
  ): Promise<DepartureComment[]> {
    return this._getEventsOfType(departureId, from, to, `${this.config.apiHost}/comments/${departureId.value}/events`);
  }

  public getLatestPermissions(departureId: UUID): Promise<DeparturePermissions> {
    return this.getPermissionsAtTime(departureId);
  }

  public getPermissionsAtTime(departureId: UUID, time?: number): Promise<DeparturePermissions> {
    return this._getEventOfTypeAtTime(departureId, `${this.config.apiHost}/permissions/${departureId.value}`, time);
  }

  public getPermissionsEvents(
    departureId: UUID,
    from: number = 0,
    to: number = Number.MAX_SAFE_INTEGER
  ): Promise<DeparturePermissions[]> {
    return this._getEventsOfType(
      departureId,
      from,
      to,
      `${this.config.apiHost}/permissions/${departureId.value}/events`
    );
  }

  public getLatestProperties(departureId: UUID): Promise<DepartureProperties> {
    return this.getPropertiesAtTime(departureId);
  }

  public getPropertiesAtTime(departureId: UUID, time?: number): Promise<DepartureProperties> {
    return this._getEventOfTypeAtTime(departureId, `${this.config.apiHost}/properties/${departureId.value}`, time);
  }

  public getPropertiesEvents(
    departureId: UUID,
    from: number = 0,
    to: number = Number.MAX_SAFE_INTEGER
  ): Promise<DepartureProperties[]> {
    return this._getEventsOfType(
      departureId,
      from,
      to,
      `${this.config.apiHost}/properties/${departureId.value}/events`
    );
  }

  public getLatestReasonCodes(departureId: UUID): Promise<DepartureReasonCodes> {
    return this.getReasonCodesAtTime(departureId);
  }

  public getReasonCodesAtTime(departureId: UUID, time?: number): Promise<DepartureReasonCodes> {
    return this._getEventOfTypeAtTime(departureId, `${this.config.apiHost}/reasonCodes/${departureId.value}`, time);
  }

  private _getEventOfTypeAtTime<T>(departureId: UUID, endpoint: string, time?: number): Promise<T> {
    if (!departureId) {
      return Promise.reject('departureId was null');
    }
    const options = time ? {params: {time: time.toString()}} : {};

    return this._http.get<T>(endpoint, options).toPromise();
  }

  private _getEventsOfType<T>(
    departureId: UUID,
    from: number = 0,
    to: number = Number.MAX_SAFE_INTEGER,
    endpoint: string
  ): Promise<T[]> {
    if (!departureId) {
      return Promise.reject('departureId was null');
    }
    const options = {params: {from: from.toString(), to: to.toString()}};
    return this._http.get<T[]>(endpoint, options).toPromise();
  }

  public onDiaryEventCreated(): Observable<DiaryEvent> {
    return this.latestDiaryEventSubject.asObservable();
  }

  public onDiarySnapshotChange(): Observable<DiarySnapshot> {
    return this.latestDiarySnapshotSubject.asObservable();
  }

  /**
   * Calculates the latest snapshot by patching the newly created event over the previous
   * snapshot.
   *
   * Can also be passed a snapshot as in this case the patch just replaces every field.
   *
   * @param snapshot either a new event or the latest snapshot.
   */
  private broadcastSnapshot(snapshot: DiarySnapshot | DiaryEvent, changedDeparture: boolean = false): void {
    if (this.snapshotService.viewingSnapshot() && !changedDeparture) {
      return;
    }

    if (snapshot && this.departureId) {
      const prevSnapshot = changedDeparture ? {} : this.latestDiarySnapshotSubject.value;
      const newSnapshot: DiarySnapshot = Object.assign({}, prevSnapshot);
      let changed: boolean = false;

      if (snapshot.workflowState && snapshot.workflowState.associatedObjectId === this.departureId.value) {
        newSnapshot.workflowState = snapshot.workflowState;
        changed = true;
      }
      if (snapshot.comment && snapshot.comment.departureId === this.departureId.value) {
        newSnapshot.comment = snapshot.comment;
        changed = true;
      }
      if (snapshot.permissions && snapshot.permissions.departureId === this.departureId.value) {
        newSnapshot.permissions = snapshot.permissions;
        changed = true;
      }
      if (snapshot.properties && snapshot.properties.departureId === this.departureId.value) {
        newSnapshot.properties = snapshot.properties;
        changed = true;
      }
      if (snapshot.reasonCodes && snapshot.reasonCodes.departureId === this.departureId.value) {
        newSnapshot.reasonCodes = snapshot.reasonCodes;
        changed = true;
      }
      if (snapshot.link && snapshot.link.departureId === this.departureId.value) {
        newSnapshot.link = snapshot.link;
        changed = true;
      }
      if (changed) {
        this.latestDiarySnapshotSubject.next(newSnapshot);
      }
    }
  }

  private broadcastEvent(event: DiaryEvent): void {
    if (this.snapshotService.viewingSnapshot()) {
      return;
    }

    if (this.departureId && this.departureId.equals(departureIdFromSnapshot(event))) {
      this.latestDiaryEventSubject.next(event);
    }
  }
}
